// ============================================
// WORKER API CLIENT
// Handles all communication with Cloudflare Worker backend
// ============================================

import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';

// Worker API base URL
const WORKER_API_URL = (import.meta as any).env?.VITE_WORKER_API_URL || 'https://codemine-api-worker.shazim-rv11-675.workers.dev';

// ============================================
// TYPES
// ============================================

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface Project {
  id: string;
  owner_id: string;
  name: string;
  description?: string;
  slug: string;
  project_type: string;
  framework?: string;
  tech_stack?: string[];
  storage_path: string;
  status: string;
  live_url?: string;
  subdomain?: string;
  file_count?: number;
  created_at: string;
  updated_at: string;
}

export interface GenerationJob {
  id: string;
  user_id: string;
  project_id?: string;
  prompt: string;
  status: 'pending' | 'planning' | 'generating' | 'complete' | 'incomplete' | 'failed';
  plan?: any;
  generated_files?: Record<string, string>;
  tokens_used?: number;
  error_message?: string;
  created_at: string;
}

export interface User {
  id: string;
  auth0_id: string;
  email: string;
  name?: string;
  role: string;
  plan: string;
  tokens_used: number;
  daily_tokens_used: number;
  monthly_tokens_used: number;
  daily_token_limit: number;
  monthly_token_limit: number;
  billing_status: string;
  created_at: string;
}

// ============================================
// API CLIENT CLASS
// ============================================

class WorkerAPIClient {
  private baseURL: string;
  private getToken: (() => Promise<string>) | null = null;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  // Set token getter function
  setTokenGetter(getter: () => Promise<string>) {
    this.getToken = getter;
  }

  // Make authenticated request
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    // Get Auth0 token if available
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.getToken) {
      try {
        const token = await this.getToken();
        headers['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        console.error('Failed to get auth token:', error);
      }
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      let errorMsg = `Request failed with status ${response.status}`;
      if (error && typeof error === 'object' && 'error' in error && typeof (error as any).error === 'string') {
        errorMsg = (error as any).error;
      }
      throw new Error(errorMsg);
    }

    return response.json();
  }

  // ============================================
  // GENERATION ENDPOINTS
  // ============================================

  async startGeneration(prompt: string, projectId?: string): Promise<ApiResponse<{
    job_id: string;
    status: string;
    plan?: any;
    project?: Project;
    tokens_used: number;
    can_continue?: boolean;
  }>> {
    return this.request('/generate/start', {
      method: 'POST',
      body: JSON.stringify({ prompt, project_id: projectId }),
    });
  }

  async continueGeneration(jobId: string): Promise<ApiResponse<{
    job_id: string;
    status: string;
    tokens_used: number;
    can_continue?: boolean;
    project?: Project;
  }>> {
    return this.request('/generate/continue', {
      method: 'POST',
      body: JSON.stringify({ job_id: jobId }),
    });
  }

  async getGenerationStatus(jobId: string): Promise<ApiResponse<{ job: GenerationJob }>> {
    return this.request(`/generate/status/${jobId}`);
  }

  // SSE stream for generation progress
  streamGeneration(jobId: string, onUpdate: (data: any) => void, onError?: (error: Error) => void) {
    const url = `${this.baseURL}/generate/stream/${jobId}`;
    const eventSource = new EventSource(url);

    eventSource.addEventListener('status', (event) => {
      try {
        const data = JSON.parse(event.data);
        onUpdate(data);
      } catch (error) {
        console.error('Failed to parse SSE data:', error);
      }
    });

    eventSource.addEventListener('complete', (event) => {
      try {
        const data = JSON.parse(event.data);
        onUpdate(data);
        eventSource.close();
      } catch (error) {
        console.error('Failed to parse SSE data:', error);
      }
    });

    eventSource.addEventListener('error', () => {
      eventSource.close();
      if (onError) {
        onError(new Error('SSE connection error'));
      }
    });

    return () => eventSource.close();
  }

  // ============================================
  // PROJECT ENDPOINTS
  // ============================================

  async getProjects(): Promise<ApiResponse<{ projects: Project[] }>> {
    return this.request('/projects');
  }

  async getProject(projectId: string): Promise<ApiResponse<{ project: Project }>> {
    return this.request(`/projects/${projectId}`);
  }

  async createProject(projectData: {
    name: string;
    description?: string;
    project_type: string;
    framework?: string;
    tech_stack?: string[];
  }): Promise<ApiResponse<{ project: Project }>> {
    return this.request('/projects', {
      method: 'POST',
      body: JSON.stringify(projectData),
    });
  }

  async updateProject(projectId: string, updates: Partial<Project>): Promise<ApiResponse<{ project: Project }>> {
    return this.request(`/projects/${projectId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteProject(projectId: string): Promise<ApiResponse> {
    return this.request(`/projects/${projectId}`, {
      method: 'DELETE',
    });
  }

  async getProjectFiles(projectId: string): Promise<ApiResponse<{ files: Record<string, string> }>> {
    return this.request(`/projects/${projectId}/files`);
  }

  async saveProjectFiles(projectId: string, files: Record<string, string>): Promise<ApiResponse<{
    uploaded: string[];
    failed?: string[];
  }>> {
    return this.request(`/projects/${projectId}/files`, {
      method: 'PUT',
      body: JSON.stringify({ files }),
    });
  }

  // ============================================
  // DEPLOYMENT ENDPOINTS
  // ============================================

  async deployProject(projectId: string): Promise<ApiResponse<{
    deployment: {
      id: string;
      subdomain: string;
      url: string;
      status: string;
    };
  }>> {
    return this.request(`/deploy/${projectId}`, {
      method: 'POST',
    });
  }

  // ============================================
  // USER ENDPOINTS
  // ============================================

  async getCurrentUser(): Promise<ApiResponse<{ user: User }>> {
    return this.request('/users/me');
  }

  async updateUserProfile(updates: Partial<User>): Promise<ApiResponse<{ user: User }>> {
    return this.request('/users/me', {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  // ============================================
  // ANALYTICS ENDPOINTS
  // ============================================

  async trackEvent(eventType: string, projectId?: string, metadata?: any): Promise<ApiResponse> {
    return this.request('/analytics/track', {
      method: 'POST',
      body: JSON.stringify({
        event_type: eventType,
        project_id: projectId,
        metadata,
      }),
    });
  }

  async getProjectAnalytics(projectId: string): Promise<ApiResponse<{ analytics: any[] }>> {
    return this.request(`/analytics/projects/${projectId}`);
  }

  // ============================================
  // ADMIN ENDPOINTS
  // ============================================

  async adminGetUsers(limit?: number): Promise<ApiResponse<{ users: User[] }>> {
    const params = limit ? `?limit=${limit}` : '';
    return this.request(`/admin/users${params}`);
  }

  async adminUpdateUser(userId: string, updates: Partial<User>): Promise<ApiResponse<{ user: User }>> {
    return this.request(`/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async adminGetProjects(limit?: number): Promise<ApiResponse<{ projects: Project[] }>> {
    const params = limit ? `?limit=${limit}` : '';
    return this.request(`/admin/projects${params}`);
  }

  async adminDeleteProject(projectId: string): Promise<ApiResponse> {
    return this.request(`/admin/projects/${projectId}`, {
      method: 'DELETE',
    });
  }

  async adminAddTokens(userId: string, amount: number, type: 'daily' | 'monthly'): Promise<ApiResponse> {
    return this.request(`/admin/users/${userId}/tokens`, {
      method: 'POST',
      body: JSON.stringify({ amount, type }),
    });
  }

  async adminMockPayment(userId: string, plan: string, amount: number): Promise<ApiResponse> {
    return this.request('/admin/payments/mock', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, plan, amount }),
    });
  }

  async adminGetStats(): Promise<ApiResponse<{ stats: any }>> {
    return this.request('/admin/stats');
  }

  // ============================================
  // HEALTH CHECK
  // ============================================

  async healthCheck(): Promise<ApiResponse<{ status: string; timestamp: string }>> {
    return this.request('/health');
  }
}

// ============================================
// SINGLETON INSTANCE
// ============================================

export const workerAPI = new WorkerAPIClient(WORKER_API_URL);

// ============================================
// REACT HOOK FOR API CLIENT
// ============================================

export function useWorkerAPI() {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  // Set token getter on mount
  React.useEffect(() => {
    if (isAuthenticated) {
      workerAPI.setTokenGetter(async () => {
        return await getAccessTokenSilently();
      });
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  return workerAPI;
}

export default workerAPI;
